import '../orbit-development.css';
import { useState, useEffect, useCallback } from 'react';
import { devApi } from '../api.js';
import { TYPE_LABEL } from '../format.js';
import {
  COLUMNS, SECTION_SORTS, TASK_SORTS, groupTasks, applyMove, sortSections, sortTasks,
  sectionSortLabel, taskSortLabel,
} from '../sections.js';
import {
  VIEWS, LIFECYCLE_LABEL, MOVED_ON, STATUSES_FOR_VIEW,
  defaultView, tasksForView, groupByEnvironment, groupByModule,
} from '../lifecycle.js';
import { exportQueue, QUEUE_FILE_NAME } from '../exportTasks.js';
import { rememberView } from '../OrbitDevelopmentApp.jsx';
import TaskForm from '../components/TaskForm.jsx';
import SectionCard from '../components/SectionCard.jsx';
import TaskTable from '../components/TaskTable.jsx';

// Every filter applies to every view — a view narrows by status, these narrow by everything
// else, and the two compose. There is one Status filter because there is one status.
const FILTERS = [
  { key: 'module',    label: 'Module' },
  { key: 'lifecycle', label: 'Status' },
  { key: 'type',      label: 'Type' },
];

function Filter({ label, value, onChange, options }) {
  return (
    <div className="o-ctrl">
      <label className="o-field-lbl">{label}</label>
      <select className="o-control" value={value} onChange={e => onChange(e.target.value)}
        style={{ minWidth: 170 }}>
        <option value="">All</option>
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SortControl({ label, value, options, onChange }) {
  return (
    <div className="o-ctrl">
      <label className="o-field-lbl">{label}</label>
      <select className="o-control" value={value} onChange={e => onChange(e.target.value)}
        style={{ minWidth: 150 }}>
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
}

/**
 * One of the four views, chosen by the URL.
 *
 * `view` is null on the module's bare path: the view a person lands on depends on their role,
 * which arrives with the first response, so the page asks to be redirected once it knows rather
 * than rendering somebody else's screen first and correcting it.
 */
export default function TasksPage({ view, onResolveView }) {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ module: '', lifecycle: '', type: '' });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(null);      // null | { sectionId }
  const [collapsed, setCollapsed] = useState({});
  const [newSection, setNewSection] = useState(null);  // null | the name being typed
  const [savingIds, setSavingIds] = useState({});      // task id -> in flight

  // Tasks moved in this sitting, newest first. Not a toast: it stays until the view or the
  // filters change, because it is the receipt for work that has left the screen and a receipt
  // that fades after four seconds is one you are not allowed to read twice.
  const [moved, setMoved] = useState([]);
  const [exporting, setExporting] = useState(false);
  // A one-line result for an action whose output leaves the page entirely — a file lands in
  // Downloads and nothing on screen changes, which without this reads as a button that did
  // nothing.
  const [exportNote, setExportNote] = useState(null);

  // Sorting is a view. It never writes, so it lives here and nowhere more permanent — coming
  // back tomorrow to an arrangement you cannot remember choosing is the confusion this whole
  // separation exists to avoid.
  const [sectionSort, setSectionSort] = useState('manual');
  const [taskSort, setTaskSort] = useState('manual');
  // My Work's own order, kept apart from the Pipeline's on purpose: the Pipeline is a filing
  // board whose order is a statement about the backlog, while My Work is a run order for the
  // day. Sharing one control made choosing a queue order rearrange the board behind it.
  const [workSort, setWorkSort] = useState('manual');

  // Which tasks the export will include. Empty means "everything in this view" — the common
  // case, and one that should not require ticking eight boxes first.
  const [picked, setPicked] = useState(() => new Set());

  // Drag state belongs to the page. A drop that crosses sections is a fact about two cards, and
  // a card that only knows what it is holding cannot complete one.
  const [drag, setDrag] = useState(null);       // { taskId, fromSection }
  const [dropAt, setDropAt] = useState(null);   // { sectionId, beforeTaskId, afterTaskId }
  const [secDrag, setSecDrag] = useState(null); // { sectionId }

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const next = await devApi.listTasks(f);
      setData(next);
      setErr(null);
      return next;
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filters); }, [load, filters]);

  // The bare module path has no view in it. Once the response says what this person's role is,
  // the URL is replaced — replaced, not pushed, so Back does not land on a path that will only
  // redirect again.
  useEffect(() => {
    if (!view && data?.role) onResolveView(defaultView(data.role));
  }, [view, data, onResolveView]);

  // So that opening a task and coming back — by saving it, deleting it, or pressing All tasks —
  // returns to this list rather than to whichever view this role opens on.
  useEffect(() => { rememberView(view); }, [view]);

  // A selection is about the list in front of you. Changing view or filters changes which tasks
  // those are, and carrying ticks across to a different list is how the wrong three get
  // exported.
  useEffect(() => { setPicked(new Set()); }, [view, filters]);

  const options = data?.options;
  const role = data?.role;
  const tasks = data?.tasks || [];
  const sections = data?.sections || [];
  const visible = tasksForView(tasks, view);
  const active = Object.values(filters).some(Boolean);
  const manual = taskSort === 'manual';
  const sectionsManual = sectionSort === 'manual';

  const choicesFor = (key) => {
    if (!options) return [];
    if (key === 'module') return options.modules;
    if (key === 'lifecycle') {
      return (options.lifecycle || []).map(s => ({ id: s, label: LIFECYCLE_LABEL[s] || s }));
    }
    return options.types.map(t => ({ id: t, label: TYPE_LABEL[t] }));
  };

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  /**
   * Returns true when the move landed, which is what tells the note field to close.
   *
   * Not optimistic, unlike the in-place edits above. A transition can be refused for a reason
   * this page cannot predict — the task moved under us, or the role does not own the move — and
   * showing it as done and then taking it back is worse than a moment's wait. The response
   * carries the updated task, so the row is replaced rather than refetched.
   */
  const transitionTask = async (task, toStatus, note) => {
    setSavingIds(s => ({ ...s, [task.id]: true }));
    try {
      const res = await devApi.transitionTask(task.id, toStatus, note);
      setData(s => ({
        ...s, tasks: s.tasks.map(t => (t.id === res.task.id ? { ...t, ...res.task } : t)),
      }));
      // A successful move almost always takes the task out of the view it was made in. Said
      // rather than shown, because showing it would mean keeping a row in a list it is no
      // longer in — which is the thing the three views exist to stop doing.
      setMoved(m => [{ id: res.task.id, title: res.task.title, to: res.task.lifecycle_status },
                     ...m.filter(x => x.id !== res.task.id)].slice(0, 4));
      setErr(null);
      return true;
    } catch (e) {
      setErr(e.message);
      return false;
    } finally {
      setSavingIds(s => { const next = { ...s }; delete next[task.id]; return next; });
    }
  };

  // ── Editing from the list ───────────────────────────────────────────────────

  // Which filter each in-place field answers to. A task edited out of the current filter has to
  // leave the list, and only a refetch knows that — the row would otherwise sit there
  // contradicting the filter above it.
  const FILTER_OF = { target_module: 'module', task_type: 'type' };

  const patchTask = async (task, fields) => {
    const snapshot = data;
    setData(s => ({ ...s, tasks: s.tasks.map(t => (t.id === task.id ? { ...t, ...fields } : t)) }));
    setSavingIds(s => ({ ...s, [task.id]: true }));
    try {
      await devApi.updateTask(task.id, fields);
      setErr(null);
      const key = FILTER_OF[Object.keys(fields)[0]];
      if (key && filters[key] && filters[key] !== Object.values(fields)[0]) await load(filters);
    } catch (e) {
      setData(snapshot);   // the row goes back to what it said before
      setErr(e.message);
    } finally {
      setSavingIds(s => { const next = { ...s }; delete next[task.id]; return next; });
    }
  };

  // ── Export ──────────────────────────────────────────────────────────────────

  const togglePick = (id) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /** Tick or clear a whole list at once — the header checkbox. */
  const pickAll = (ids, on) => setPicked(prev => {
    const next = new Set(prev);
    ids.forEach(id => (on ? next.add(id) : next.delete(id)));
    return next;
  });

  /**
   * The task ids in the order this view is rendering them.
   *
   * Built from the same helpers the render below uses, rather than re-deriving the grouping:
   * one definition of "what order is this in", used twice.
   */
  const displayOrder = () => {
    if (view === 'pipeline') {
      return sortSections(groupTasks(tasks, sections), sectionSort)
        .flatMap(g => sortTasks(tasksForView(g.tasks, 'pipeline'), taskSort, options))
        .map(t => t.id);
    }
    if (view === 'qa-queue') {
      return groupByEnvironment(visible, options?.environments)
        .flatMap(env => groupByModule(env.tasks, options?.modules))
        .flatMap(g => g.tasks)
        .map(t => t.id);
    }
    return workGroups().flatMap(g => g.tasks).map(t => t.id);
  };

  /**
   * My Work's groups, in the order it renders them.
   *
   * Module grouping stays whatever the sort is — it is what makes a run of related work land
   * together — so a sort orders tasks WITHIN each module rather than flattening the list. On
   * `manual` the order is the stored one, with anything QA sent back pulled to the top.
   */
  const workGroups = () => groupByModule(visible, options?.modules).map(g => ({
    ...g,
    tasks: workSort === 'manual' ? g.tasks : sortTasks(g.tasks, workSort, options),
  }));

  /**
   * The current view as a workbook.
   *
   * The statuses are asked for explicitly rather than the server being told "My Work": the views
   * are defined in lifecycle.js, and a second definition on the server is a second place to
   * change when one of them moves.
   */
  const runExport = async () => {
    setExporting(true);
    try {
      const payload = await devApi.exportTasks({
        ...filters,
        statuses: (STATUSES_FOR_VIEW[view] || []).join(','),
      });
      const { tasks: n, attachments } = exportQueue({
        payload,
        viewLabel: VIEWS.find(v => v.id === view)?.label || view,
        // The queue is worked top to bottom, so its order has to be THIS order — the one on
        // screen, grouping and pinned rejections included. Only this component knows it.
        orderedIds: displayOrder(),
        // Ticked rows only, when any are ticked. Nothing ticked means the whole view, which is
        // what someone who never touched a checkbox expects.
        includeIds: picked.size ? [...picked] : null,
      });
      setErr(null);
      setMoved([]);
      setExportNote(`Exported ${n}${picked.size ? ' selected' : ''} task${n === 1 ? '' : 's'}`
        + `${attachments ? ` and ${attachments} attachment${attachments === 1 ? '' : 's'}` : ''}`
        + ` to ${QUEUE_FILE_NAME}, in the order shown here.`
        + (attachments
          ? ` Attachment links stop working in ${payload.url_expiry_days} days.`
          : ''));
    } catch (e) {
      setErr(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  // ── Section CRUD ────────────────────────────────────────────────────────────

  const run = async (fn) => {
    try { await fn(); setErr(null); return true; }
    catch (e) { setErr(e.message); return false; }
  };

  const createSection = async (name) => {
    const ok = await run(async () => {
      const { section } = await devApi.createSection(name.trim());
      setData(s => ({ ...s, sections: [...s.sections, section] }));
    });
    if (ok) setNewSection(null);
  };

  const renameSection = (section, name) => run(async () => {
    const res = await devApi.renameSection(section.id, name);
    setData(s => ({
      ...s, sections: s.sections.map(x => (x.id === section.id ? res.section : x)),
    }));
  });

  const deleteSection = (section) => run(async () => {
    await devApi.deleteSection(section.id);
    // The tasks are not deleted — they move to Unsectioned, and only the server knows where in
    // it they landed, so this is the one section action that refetches.
    await load(filters);
  });

  const reorderSections = (orderedIds) => run(async () => {
    const res = await devApi.setSectionsOrder(orderedIds);
    setData(s => ({ ...s, sections: res.sections }));
  });

  const moveSection = (sectionId, delta) => {
    const ids = sections.map(s => s.id);
    const from = ids.indexOf(sectionId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    setData(s => ({ ...s, sections: ids.map(id => s.sections.find(x => x.id === id)) }));
    reorderSections(ids);
  };

  // ── Drag and drop ───────────────────────────────────────────────────────────

  const onTaskDragStart = (e, task, sectionId) => {
    e.stopPropagation();
    setDrag({ taskId: task.id, fromSection: sectionId });
    e.dataTransfer.effectAllowed = 'move';
    // Set only because Firefox refuses to start a drag without data on the transfer. It is
    // never read back — React state is what the drop is resolved from.
    e.dataTransfer.setData('text/plain', task.id);
  };

  const onRowDragOver = (e, sectionId, task) => {
    if (!drag) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const above = e.clientY < rect.top + rect.height / 2;
    setDropAt(above
      ? { sectionId, beforeTaskId: task.id, afterTaskId: null }
      : { sectionId, beforeTaskId: null, afterTaskId: task.id });
  };

  const onSectionDragOver = (e, sectionId) => {
    if (drag) {
      e.preventDefault();
      // Only claim the drop if no row under the pointer has already claimed it. This is what
      // makes an empty section — which has no rows at all — a valid target.
      setDropAt(prev => (prev && prev.sectionId === sectionId ? prev
        : { sectionId, beforeTaskId: null, afterTaskId: null }));
    } else if (secDrag && secDrag.sectionId !== sectionId && sectionId) {
      e.preventDefault();
    }
  };

  const clearDrag = () => { setDrag(null); setDropAt(null); setSecDrag(null); };

  const commitDrop = async (sectionId) => {
    if (secDrag) {
      const ids = sections.map(s => s.id);
      const from = ids.indexOf(secDrag.sectionId);
      const to = ids.indexOf(sectionId);
      clearDrag();
      if (from < 0 || to < 0 || from === to) return;
      ids.splice(to, 0, ids.splice(from, 1)[0]);
      setData(s => ({ ...s, sections: ids.map(id => s.sections.find(x => x.id === id)) }));
      reorderSections(ids);
      return;
    }

    const moved = drag;
    const at = dropAt;
    clearDrag();
    if (!moved || !at) return;

    // "After that row" is expressed to the server as "before the one below it", because a slot
    // is only ever named by the task that will follow it.
    const bucket = groupTasks(tasks, sections).find(g => (g.section.id || null) === (at.sectionId || null));
    let before = at.beforeTaskId;
    if (!before && at.afterTaskId) {
      const list = bucket?.tasks || [];
      const i = list.findIndex(t => t.id === at.afterTaskId);
      before = i >= 0 && i + 1 < list.length ? list[i + 1].id : null;
    }
    if (before === moved.taskId) before = null;

    const snapshot = data;
    setData(s => ({ ...s, tasks: applyMove(s.tasks, moved.taskId, at.sectionId, before) }));
    try {
      const res = await devApi.moveTaskToSection(moved.taskId, at.sectionId, before);
      setData(s => ({
        ...s,
        sections: res.sections,
        tasks: s.tasks.map(t => (t.id === res.task.id ? res.task : t)),
      }));
      setErr(null);
    } catch (e) {
      setData(snapshot);   // put it back exactly where it was
      setErr(e.message);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (creating) {
    return (
      <main className="o-page">
        <TaskForm
          options={options}
          sectionId={creating.sectionId}
          onCancel={() => setCreating(null)}
          // Back to the list, not into the task just written. Opening a task is now opening it
          // for editing, and handing someone the edit form for the thing they have this second
          // finished filling in is a loop, not a destination.
          onSaved={() => { setCreating(null); load(filters); }}
        />
      </main>
    );
  }

  const groups = sortSections(groupTasks(tasks, sections), sectionSort);

  return (
    <main className="o-page">
      {/* The view's name is in the nav above, lit; what is worth repeating here is what this
          view is FOR, and how much is in it. None of the four is a place a task lives — the
          same task is in My Work today and the QA Queue tomorrow without anything having been
          moved, which is the whole reason the section stopped carrying the workflow.

          "New section" belongs to the Pipeline: it is the view that shows the filing. */}
      <div className="od-view-heading">
        <span className="o-badge o-grey">{visible.length}</span>
        <span className="o-note od-view-description">
          {VIEWS.find(v => v.id === view)?.note}
        </span>
        {/* Ghost, not primary: exporting is never the reason someone opened this screen, and
            the Pipeline already has a filled button that is. */}
        <button className="o-btn o-btn-ghost" disabled={exporting || !visible.length}
          title={visible.length
            ? `Download this view as ${QUEUE_FILE_NAME} — the prompts, in this order, `
              + 'with instructions for working through them'
            : 'Nothing to export'}
          onClick={runExport}>
          {exporting ? 'Building…' : `Export ${QUEUE_FILE_NAME}`}
        </button>
        {view === 'pipeline' && (
          <button className="o-btn o-btn-primary" onClick={() => setNewSection('')}>
            New section
          </button>
        )}
      </div>

      {exportNote && (
        <div className="o-hint" style={{ display: 'flex', gap: 12, alignItems: 'flex-start',
          marginBottom: 10 }}>
          <span style={{ flex: 1 }}>{exportNote}</span>
          <button className="o-btn o-btn-ghost o-btn-sm"
            onClick={() => setExportNote(null)}>Dismiss</button>
        </div>
      )}

      <div className="o-panel" style={{ marginBottom: 10 }}>
        <div className="o-ctrl-row o-ctrl-row-tight">
          {FILTERS.map(f => (
            <Filter key={f.key} label={f.label} value={filters[f.key]}
              options={choicesFor(f.key)}
              onChange={v => { setFilters(s => ({ ...s, [f.key]: v })); setMoved([]); }} />
          ))}
          {/* Ordering is the Pipeline's, because only it shows the stored order. The working
              views have an order of their own that is not a preference: what QA sent back
              first. */}
          {view === 'pipeline' && (
            <>
              <SortControl label="Order sections by" value={sectionSort}
                options={SECTION_SORTS} onChange={setSectionSort} />
              <SortControl label="Order tasks by" value={taskSort}
                options={TASK_SORTS} onChange={setTaskSort} />
            </>
          )}
          {/* My Work's own, and only My Work's: this is the order the exported queue runs in,
              and it must not move the Pipeline's board around to set it. */}
          {view === 'my-work' && (
            <SortControl label="Order tasks by" value={workSort}
              options={TASK_SORTS} onChange={setWorkSort} />
          )}
          {active && (
            <div className="o-ctrl" style={{ justifyContent: 'flex-end' }}>
              <button className="o-btn o-btn-ghost"
                onClick={() => setFilters({ module: '', lifecycle: '', type: '' })}>
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>

      {err && <div className="o-state o-error" style={{ textAlign: 'left' }}>{err}</div>}
      {loading && !data && <div className="o-state">Loading…</div>}

      {/* Where the rows that just disappeared went. */}
      {moved.length > 0 && (
        <div className="o-hint" style={{
          display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10,
        }}>
          <div style={{ flex: 1 }}>
            {moved.map(m => (
              <div key={m.id} style={{ marginBottom: 2 }}>
                <b>{m.title}</b> {MOVED_ON[m.to] || `is now ${LIFECYCLE_LABEL[m.to] || m.to}`}.
                {!tasksForView([{ lifecycle_status: m.to }], view).length
                  && ' It has left this view.'}
              </div>
            ))}
          </div>
          <button className="o-btn o-btn-ghost o-btn-sm" onClick={() => setMoved([])}>
            Dismiss
          </button>
        </div>
      )}

      {view === 'pipeline' && (!manual || !sectionsManual) ? (
        <p className="o-hint">
          <b>Sorted by {!sectionsManual ? `${sectionSortLabel(sectionSort)} (sections)` : ''}
          {!sectionsManual && !manual ? ' and ' : ''}
          {!manual ? taskSortLabel(taskSort) : ''}.</b>{' '}
          Dragging is off while a sort is on — this is a view, and the stored order has not
          changed. Switch back to Manual order to rearrange.
        </p>
      ) : null}

      {newSection !== null && (
        <div className="o-panel" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 8 }}>
          <input className="o-control" autoFocus value={newSection} style={{ maxWidth: 320 }}
            placeholder="Section name, e.g. QA session — 12 Oct"
            onChange={e => setNewSection(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newSection.trim()) createSection(newSection);
              if (e.key === 'Escape') setNewSection(null);
            }} />
          <button className="o-btn o-btn-primary" disabled={!newSection.trim()}
            onClick={() => createSection(newSection)}>Create section</button>
          <button className="o-btn o-btn-ghost" onClick={() => setNewSection(null)}>Cancel</button>
        </div>
      )}

      {/* ── My Work ──
          The Pipeline's table, grouped by module instead of by section, and with every way of
          rearranging it removed. The groups are the module column read back, not filing anyone
          chose, so there is nothing here to reorder, rename or drop into. */}
      {data && view === 'my-work' && (
        <TaskTable
          groups={workGroups()}
          selectable
          picked={picked}
          onPick={togglePick}
          onPickAll={pickAll}
          options={options}
          onTransition={transitionTask}
          savingIds={savingIds}
          empty={active
            ? 'No tasks match these filters.'
            : role?.can_develop
              ? 'Nothing waiting on you. Anything QA sends back will appear here first.'
              : 'Nothing here — moving tasks through development is a developer’s job, '
                + 'and you hold ' + (role?.name || 'no role') + '.'}
        />
      )}

      {/* ── QA Queue ──
          Grouped by environment rather than merely sorted by it: the first thing a tester needs
          to settle is which box they are testing, and a mixed list makes that a per-row read. */}
      {data && view === 'qa-queue' && (
        <>
          {/* Environment first, module inside it. Which box is being tested is the thing a
              tester has to settle before anything else — it decides where they are looking —
              and within one environment the module is how the testing is actually batched.

              Both environments are always rendered, empty or not. An empty queue is a real
              answer and a common one; a heading that disappears when it has nothing in it
              reads as a queue that has gone missing, which is exactly how this looked. */}
          {groupByEnvironment(visible, options?.environments).map(env => (
            <section key={env.environment} style={{ marginBottom: 24 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8,
              }}>
                <span className="o-eyebrow">{env.label}</span>
                <span className="o-badge o-grey">{env.tasks.length}</span>
                <span className="o-note" style={{ margin: 0 }}>
                  {/* Spelled out because "Pass" reads like "promote", and it is not: passing
                      records a verification, it does not deploy anything. The task then waits
                      for the release to actually reach production before it can be checked
                      there. */}
                  {env.environment === 'test'
                    ? 'On test, not on production. Passing records the verification — the task '
                      + 'then waits for a developer to deploy and mark it, and comes back below.'
                    : 'Live on production. Passing here finishes the task for good.'}
                </span>
              </div>
              <TaskTable
                groups={groupByModule(env.tasks, options?.modules)}
                options={options}
                onTransition={transitionTask}
                savingIds={savingIds}
                empty={active
                  ? 'No tasks match these filters.'
                  : env.environment === 'test'
                    ? 'Nothing waiting on test QA. Work lands here when a developer marks it ready.'
                    : 'Nothing waiting on production QA. Work lands here once a release that '
                      + 'passed test reaches production.'}
              />
            </section>
          ))}
          {!role?.can_qa && (
            <p className="o-hint">
              <b>You can read this queue but not act on it.</b> Passing and rejecting belong to
              QA; you hold {role?.name || 'no role'}.
            </p>
          )}
        </>
      )}

      {/* ── Completed ──
          Its own page rather than the bottom of the Pipeline. Finished work is read for a
          different reason — what shipped, and when — and a backlog you scroll past to reach it
          serves neither question. Read-only by construction: done is terminal, so no task here
          offers a move. */}
      {data && view === 'completed' && (
        <TaskTable
          groups={groupByModule(visible, options?.modules)}
          options={options}
          onTransition={transitionTask}
          savingIds={savingIds}
          empty={active
            ? 'No tasks match these filters.'
            : 'Nothing completed yet. A task lands here when QA signs it off on production.'}
        />
      )}

      {data && view === 'pipeline' && tasks.length === 0 && sections.length === 0 && (
        <div className="o-panel">
          <div className="o-state">
            {active
              ? 'No tasks match these filters.'
              : 'No tasks yet. Create a section first — every task is filed in one.'}
          </div>
        </div>
      )}

      {data && view === 'pipeline' && (tasks.length > 0 || sections.length > 0) && (
        <div className="od-pipeline-scroll o-scrollport" role="region" aria-label="Pipeline tasks" tabIndex={0}><div className="od-pipeline-content">
          {/* The header is rendered once, above the cards, over the same colgroup every section
              uses — otherwise each table sizes its own columns and they stop lining up. */}
          <table className="o-tbl o-tbl-tight" style={{ marginBottom: -1 }}>
            <colgroup>
              {COLUMNS.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
            </colgroup>
            <thead>
              <tr>{COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}</tr>
            </thead>
          </table>

          {groups.map(({ section, tasks: rows }) => (
            <SectionCard
              key={section.id || 'unsectioned'}
              section={section}
              tasks={sortTasks(tasksForView(rows, 'pipeline'), taskSort, options)}
              options={options}
              overlaps={data.overlaps}
              collapsed={!!collapsed[section.id || 'unsectioned']}
              onToggle={() => setCollapsed(s => ({
                ...s, [section.id || 'unsectioned']: !s[section.id || 'unsectioned'],
              }))}
              canDrag={manual}
              sortedOff={!manual}
              drag={drag}
              dropAt={dropAt && dropAt.sectionId === (section.id || null) ? dropAt : null}
              sectionDraggable={sectionsManual}
              onRename={renameSection}
              onDelete={deleteSection}
              onFullForm={(s) => setCreating({ sectionId: s.id })}
              onPatchTask={patchTask}
              onTransitionTask={transitionTask}
              savingIds={savingIds}
              onTaskDragStart={onTaskDragStart}
              onRowDragOver={onRowDragOver}
              onSectionDragOver={(e) => onSectionDragOver(e, section.id || null)}
              onSectionDragStart={(e) => {
                e.stopPropagation();
                setSecDrag({ sectionId: section.id });
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', section.id);
              }}
              onMoveUp={() => moveSection(section.id, -1)}
              onMoveDown={() => moveSection(section.id, 1)}
              onDragEnd={clearDrag}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); commitDrop(section.id || null); }}
            />
          ))}
        </div></div>
      )}
    </main>
  );
}
